import React from 'react';

import { gql } from '@apollo/client';
import { Box } from '@mui/material';

import { ActivityLogAgentReasoning_ActivityLogFragment } from 'src/generated/mloop-graphql';

import { FCWithFragments } from 'src/components/types';

import { useIsLastActivityLog } from 'src/entities/ActivityLog/ActivityLogListItemContext';
import ActivityLogBase from 'src/entities/ActivityLog/Helpers/ActivityLogBase';
import ActivityLogCommentBody from 'src/entities/ActivityLog/Helpers/ActivityLogCommentBody';

import useIsAiTaskEnabled from 'src/hooks/useIsAiTaskEnabled';

interface Fragments {
  activityLog: ActivityLogAgentReasoning_ActivityLogFragment;
}

type ActivityLogContentShape = {
  markdown?: string | null;
};

const ActivityLogAgentReasoning: FCWithFragments<Fragments> = ({ activityLog }) => {
  const aiTaskEnabled = useIsAiTaskEnabled();
  const isLastActivityLog = useIsLastActivityLog();
  if (!aiTaskEnabled) {
    return null;
  }

  const { details } = activityLog;
  // eslint-disable-next-line no-underscore-dangle
  const isReasoningDetails = details?.__typename === 'ActivityLogAgentReasoningDetails';
  const reasoning = isReasoningDetails ? details.reasoning : '';
  const markdown = isReasoningDetails
    ? (details as typeof details & { content?: ActivityLogContentShape | null }).content?.markdown || reasoning || null
    : null;
  const actionLabel = 'thought for a few seconds';

  return (
    <ActivityLogBase
      activityLog={activityLog}
      actionLabel={actionLabel}
      collapsible
      defaultExpanded={isLastActivityLog}
    >
      {reasoning ? (
        <Box
          sx={{
            mt: 0.25,
            color: 'text.secondary',
            '& .activity-log-comment-body__editor': {
              color: 'text.secondary',
              fontSize: (theme) => theme.typography.caption.fontSize,
              lineHeight: (theme) => theme.typography.caption.lineHeight,
              fontWeight: (theme) => theme.typography.caption.fontWeight,
            },
          }}
        >
          <ActivityLogCommentBody legacyText={reasoning} markdown={markdown} />
        </Box>
      ) : null}
    </ActivityLogBase>
  );
};

ActivityLogAgentReasoning.fragments = {
  activityLog: gql`
    ${ActivityLogBase.fragments.activityLog}
    fragment ActivityLogAgentReasoning_activityLog on ActivityLog {
      id
      details {
        ... on ActivityLogAgentReasoningDetails {
          __typename
          reasoning
          content {
            markdown
            plaintext
            formatVersion
            editorStateJson
          }
        }
      }
      ...ActivityLogBase_activityLog
    }
  `,
};

export default ActivityLogAgentReasoning;
